import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/ilajly-logo-icon.png";

interface AnimatedLogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  showText?: boolean;
  animate?: boolean;
}

const sizes: Record<string, { className: string; width: number; height: number }> = {
  sm: { className: "h-8", width: 32, height: 32 },
  md: { className: "h-12", width: 48, height: 48 },
  lg: { className: "h-16", width: 64, height: 64 },
  xl: { className: "h-20", width: 80, height: 80 },
  hero: { className: "h-28", width: 112, height: 112 },
};

export function AnimatedLogo({ size = "md", animate = true }: AnimatedLogoProps) {
  const { className: sizeClass, width, height } = sizes[size];
  const isHero = size === "hero";

  return (
    <motion.div
      className="flex items-center gap-3"
      initial={animate ? { opacity: 0, scale: 0.8 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Animated Icon with Glow */}
      <motion.div
        className="relative"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full bg-primary/30 blur-xl"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Main logo icon */}
        <motion.img
          src={logo}
          alt="ilajly"
          width={width}
          height={height}
          {...(isHero ? { fetchPriority: "high" as const } : {})}
          className={`${sizeClass} w-auto relative z-10 drop-shadow-2xl`}
          initial={animate ? { rotate: -10, opacity: 0 } : false}
          animate={{ rotate: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
    </motion.div>
  );
}

// Full-screen loading animation
export function LoadingScreen() {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/10"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-primary/20"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Center content */}
      <div className="relative flex flex-col items-center">
        <AnimatedLogo size="hero" />

        {/* Loading bar */}
        <motion.div
          className="mt-8 w-48 h-1 bg-muted rounded-full overflow-hidden"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <motion.div
            className="h-full bg-gradient-to-r from-primary to-accent-foreground rounded-full"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          className="mt-4 text-sm text-muted-foreground font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Real doctors. Real care.
        </motion.p>
      </div>
    </motion.div>
  );
}
